import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { LESSONS, STAGES, stageOf } from "@/lib/academyLessons";
import { COUNTRIES, getStatesFor } from "@/data/locations";
import {
  CheckCircle2, Lock, ChevronRight, ExternalLink, Smartphone, Monitor,
  PartyPopper, ArrowRight, Clock, Check, GraduationCap,
} from "lucide-react";

const GRAD_IMG = "https://images.unsplash.com/photo-1503266980949-bd30d04d0b7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function LessonCard({ lesson, idx, done, onDone, busy, inviteUrl, inviteLoading, isNext }) {
  const stage = stageOf(lesson);
  const Icon = lesson.icon;
  return (
    <div className="rounded-2xl overflow-hidden bg-white fade-up"
      style={{ border: `1px solid ${stage.accent}30`, boxShadow: "0 16px 48px -16px rgba(15,18,38,0.18)" }}
      data-testid={`lesson-card-${idx}`}>
      {/* Visual header */}
      <div className="px-6 md:px-10 py-8 md:py-10" style={{ background: `linear-gradient(135deg, ${stage.accent}12, #FbFcFf)` }}>
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: stage.accent }}>
            Stage {stage.id} · {stage.name}
          </span>
          <span className="text-[11px] font-mono text-ink-3 flex items-center gap-1"><Clock size={11} /> {lesson.duration}</span>
        </div>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${stage.accent}1a` }}>
            <Icon size={24} style={{ color: stage.accent }} />
          </div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-ink">{lesson.title}</h2>
            <p className="text-sm md:text-[15px] text-ink-2 mt-1">{lesson.goal}</p>
          </div>
        </div>
      </div>

      {/* Lesson video */}
      {lesson.video && (
        <div className="px-6 md:px-10 pt-6">
          <video
            controls playsInline preload="metadata"
            poster={lesson.video.poster}
            src={lesson.video.src}
            className="w-full rounded-xl"
            style={{ aspectRatio: "16/9", background: "#0F1226", boxShadow: "0 16px 48px -16px rgba(15,18,38,0.35)" }}
            data-testid={`lesson-video-${idx}`}
          />
          {lesson.video.note && (
            <div className="mt-2.5 text-[12px] font-mono text-ink-3">{lesson.video.note}</div>
          )}
        </div>
      )}

      <div className="px-6 md:px-10 py-6 max-w-2xl">
        {/* Steps */}
        <ol className="space-y-3.5">
          {lesson.steps.map((s, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                style={{ background: `${stage.accent}15`, color: stage.accent }}>{i + 1}</span>
              <span className="text-[15px] text-ink leading-relaxed">{s}</span>
            </li>
          ))}
        </ol>

        {(lesson.mobile || lesson.web) && (
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            {lesson.mobile && (
              <div className="rounded-xl px-4 py-3 text-[13px] text-ink-2 flex gap-2.5 items-start" style={{ background: "rgba(15,18,38,0.04)" }}>
                <Smartphone size={15} className="shrink-0 mt-0.5" style={{ color: stage.accent }} />
                <span><b className="text-ink">Phone:</b> {lesson.mobile}</span>
              </div>
            )}
            {lesson.web && (
              <div className="rounded-xl px-4 py-3 text-[13px] text-ink-2 flex gap-2.5 items-start" style={{ background: "rgba(15,18,38,0.04)" }}>
                <Monitor size={15} className="shrink-0 mt-0.5" style={{ color: stage.accent }} />
                <span><b className="text-ink">Desktop:</b> {lesson.web}</span>
              </div>
            )}
          </div>
        )}

        {lesson.link && (
          <a href={lesson.link.href} target="_blank" rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: stage.accent }}
            data-testid={`lesson-link-${idx}`}>
            {lesson.link.label} <ExternalLink size={14} />
          </a>
        )}

        {/* Personal CaryPact invitation (lesson 8) */}
        {lesson.invite && (
          <div className="mt-6 rounded-xl p-5" style={{ background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.2)" }}
            data-testid="invite-block">
            <div className="text-sm font-semibold text-ink mb-2">Your personal invitation</div>
            {inviteLoading ? (
              <div className="text-sm text-ink-2">Fetching your invitation…</div>
            ) : inviteUrl ? (
              <a href={inviteUrl} target="_blank" rel="noreferrer" className="btn-primary h-11 inline-flex px-5 text-sm"
                style={{ background: "#7C3AED" }} data-testid="invite-open-btn">
                Open my CaryPact invitation <ExternalLink size={15} />
              </a>
            ) : (
              <div className="text-sm text-ink-2">Your coach will send your personal invitation shortly — you can mark this lesson done once you've registered.</div>
            )}
          </div>
        )}

        {lesson.note && (
          <div className="mt-5 text-[13px] text-ink-2 rounded-xl px-4 py-3 leading-relaxed" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}>
            {lesson.note}
          </div>
        )}
      </div>

      {/* Mark as done */}
      <div className="px-6 md:px-10 pb-7">
        {done ? (
          <div className="flex items-center gap-2 text-[15px] font-semibold" style={{ color: "#00863A" }} data-testid={`lesson-done-badge-${idx}`}>
            <CheckCircle2 size={19} /> Completed — nice work
          </div>
        ) : isNext ? (
          <button onClick={() => onDone(idx)} disabled={busy}
            className="w-full md:w-auto h-12 px-8 rounded-full font-bold text-white text-[15px] inline-flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:scale-[1.02] disabled:opacity-50"
            style={{ background: "#00863A", boxShadow: "0 10px 28px -8px rgba(0,134,58,0.5)" }}
            data-testid={`lesson-done-btn-${idx}`}>
            <Check size={18} /> {busy ? "Saving…" : "Mark as done"}
          </button>
        ) : (
          <div className="text-[13px] text-ink-3 font-mono flex items-center gap-2"><Lock size={13} /> Finish the previous lessons to unlock this one.</div>
        )}
      </div>
    </div>
  );
}

function Graduation({ me, hdr, onUpdate }) {
  const caps = me.captures || {};
  const [form, setForm] = useState({
    country: caps.country || "", state: caps.state || "",
    carypact_link: caps.carypact_link || "", wallet_address: caps.wallet_address || "",
  });
  const [saving, setSaving] = useState(false);
  const requested = !!me.access_requested_at;
  const complete = !!me.captures_complete;
  const filled = ["country", "state", "carypact_link", "wallet_address"].filter((k) => (form[k] || "").trim()).length;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/academy/captures", form, hdr);
      let merged = data;
      if (data.captures_complete && !data.access_requested_at) {
        try {
          const r = await api.post("/academy/request-access", {}, hdr);
          merged = { ...data, ...r.data };
        } catch { /* non-blocking */ }
      }
      onUpdate(merged);
      if (merged.captures_complete) toast.success("Graduation form submitted — your coach has been notified! 🎓", { duration: 8000 });
      else toast.success("Saved — fill in the rest to graduate.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl overflow-hidden fade-up" style={{ boxShadow: "0 24px 70px -24px rgba(15,18,38,0.35)" }} data-testid="graduation-panel">
      <div className="relative">
        <img src={GRAD_IMG} alt="Congratulations" className="w-full object-cover" style={{ height: 220 }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,18,38,0.15), rgba(15,18,38,0.75))" }} />
        <div className="absolute bottom-5 left-6 md:left-10 text-white">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] mb-1.5"><PartyPopper size={14} /> Lessons complete</div>
          <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight">You did it, {me.name?.split(" ")[0]}.</h2>
        </div>
      </div>
      <div className="bg-white px-6 md:px-10 py-7">
        <div className="flex flex-wrap gap-2 mb-6">
          {["Wallet set up", "Funded", "Connected to CaryPact", "Earning strategy chosen"].map((t, i) => (
            <span key={i} className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
              style={{ background: "rgba(0,134,58,0.08)", color: "#00863A", border: "1px solid rgba(0,134,58,0.2)" }}>
              <CheckCircle2 size={13} /> {t}
            </span>
          ))}
        </div>
        {complete && requested ? (
          <div className="rounded-xl p-5" style={{ background: "rgba(0,134,58,0.06)", border: "1px solid rgba(0,134,58,0.2)" }} data-testid="access-requested-state">
            <div className="font-semibold text-ink mb-1 flex items-center gap-1.5"><CheckCircle2 size={16} style={{ color: "#00863A" }} /> Graduation form submitted ✓</div>
            <p className="text-sm text-ink-2">Your coach has been notified and will provision your BotChainCP member access. Watch your inbox for your login credentials — usually within a day.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-lg font-bold text-ink flex items-center gap-2"><GraduationCap size={18} style={{ color: "#4060FF" }} /> Graduation form — one last step</h3>
              <span className="text-xs font-mono font-bold text-ink-2" data-testid="grad-captures-count">{filled} / 4</span>
            </div>
            <p className="text-sm text-ink-2 mb-5 max-w-xl">
              These four details unlock your BotChainCP member access. The moment they're in, your coach
              provisions your login and you take your place on the team chart under your sponsor.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <label className="block">
                <span className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-1 block">Country</span>
                <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value, state: "" })}
                  className="w-full h-11 px-3 rounded-xl text-sm bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
                  style={{ border: "1px solid rgba(15,18,38,0.14)" }} data-testid="grad-country">
                  <option value="">Select country…</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-1 block">State / Province</span>
                <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} disabled={!form.country}
                  className="w-full h-11 px-3 rounded-xl text-sm bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF] disabled:opacity-50"
                  style={{ border: "1px solid rgba(15,18,38,0.14)" }} data-testid="grad-state">
                  <option value="">{form.country ? "Select state…" : "Pick a country first"}</option>
                  {getStatesFor(form.country).map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <label className="block mb-3">
              <span className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-1 block">Your CaryPact invite link</span>
              <input value={form.carypact_link} onChange={(e) => setForm({ ...form, carypact_link: e.target.value })}
                placeholder="https://app.carypact.com/?inviteCode=…"
                className="w-full h-11 px-3 rounded-xl text-sm font-mono bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
                style={{ border: "1px solid rgba(15,18,38,0.14)" }} data-testid="grad-link" />
              <span className="text-[11px] text-ink-3 mt-1 block">In CaryPact: <b>User Center → Invite Friends → Copy invite link</b>.</span>
            </label>
            <label className="block mb-5">
              <span className="text-[11px] font-mono uppercase tracking-wider text-ink-3 mb-1 block">Your wallet address</span>
              <input value={form.wallet_address} onChange={(e) => setForm({ ...form, wallet_address: e.target.value })}
                placeholder="0x…"
                className="w-full h-11 px-3 rounded-xl text-sm font-mono bg-white outline-none focus-visible:ring-2 focus-visible:ring-[#4060FF]"
                style={{ border: "1px solid rgba(15,18,38,0.14)" }} data-testid="grad-wallet" />
              <span className="text-[11px] text-ink-3 mt-1 block">In TP Wallet: tap <b>BOT → Receive → Copy</b>.</span>
            </label>
            <button onClick={save} disabled={saving} className="btn-primary h-12 px-8 text-[15px]" data-testid="grad-save-btn">
              {saving ? "Saving…" : filled === 4 ? <>Submit my graduation form <ArrowRight size={16} /></> : "Save my details"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Academy() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState({ url: null, loading: false, fetched: false });
  const cardRef = useRef(null);

  const urlToken = params.get("t");
  if (urlToken) localStorage.setItem("academy_token", urlToken);
  useEffect(() => {
    if (urlToken) setParams({}, { replace: true });
  }, [urlToken]); // eslint-disable-line
  const token = localStorage.getItem("academy_token");
  const hdr = { headers: { "X-Academy-Token": token } };

  const load = useCallback(async () => {
    if (!token) { navigate("/start"); return; }
    try {
      const { data } = await api.get("/academy/me", hdr);
      setMe(data);
      setActive((a) => (a === null ? Math.min(data.progress.length, LESSONS.length - 1) : a));
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("academy_token"); navigate("/start"); }
      else setLoadErr("Could not load your training — check your connection and refresh.");
    }
  }, [token]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  // Fetch personal invite link when the invite lesson is open
  useEffect(() => {
    if (me && active !== null && LESSONS[active]?.invite && !invite.fetched) {
      setInvite((v) => ({ ...v, loading: true }));
      api.get("/academy/invite-link", hdr)
        .then(({ data }) => setInvite({ url: data.url, loading: false, fetched: true }))
        .catch(() => setInvite({ url: null, loading: false, fetched: true }));
    }
  }, [me, active]); // eslint-disable-line

  const markDone = async (idx) => {
    setBusy(true);
    try {
      const { data } = await api.post("/academy/complete", { lesson: idx }, hdr);
      setMe((m) => ({ ...m, ...data }));
      if (idx < LESSONS.length - 1) {
        toast.success(`Lesson ${idx + 1} complete — next lesson unlocked!`);
        setActive(idx + 1);
        setTimeout(() => cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      } else {
        toast.success("Training complete! 🎓");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (loadErr) return <div className="min-h-screen flex items-center justify-center text-ink-2 text-sm px-6 text-center">{loadErr}</div>;
  if (!me) return <div className="min-h-screen flex items-center justify-center text-ink-3 font-mono text-sm">Loading your academy…</div>;

  const doneCount = me.progress.length;
  const nextIdx = doneCount;
  const pct = (doneCount / LESSONS.length) * 100;

  return (
    <div className="min-h-screen pb-16" data-testid="academy-page">
      {/* Sticky progress header */}
      <header className="sticky top-0 z-40 px-4 md:px-10 pt-3">
        <div className="max-w-[900px] mx-auto rounded-2xl px-4 md:px-6 py-3" style={{
          background: "rgba(255,255,255,0.78)", backdropFilter: "blur(16px)",
          border: "1px solid rgba(15,18,38,0.08)", boxShadow: "0 8px 32px rgba(15,18,38,0.08)",
        }}>
          <div className="flex items-center justify-between mb-2">
            <Link to="/start" className="flex items-center gap-2"><Logo size="sm" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4060FF] hidden sm:inline">CP Academy</span>
            </Link>
            <span className="text-xs font-mono font-bold text-ink" data-testid="academy-progress-count">
              {me.completed ? "Complete 🎓" : `Lesson ${Math.min(nextIdx + 1, 12)} of ${LESSONS.length}`}
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.07)" }}>
            <div className="h-full rounded-full transition-[width] duration-700 ease-out"
              style={{ width: `${pct}%`, background: "linear-gradient(90deg, #4060FF, #7C3AED)" }} data-testid="academy-progress-bar" />
          </div>
        </div>
      </header>

      <main className="px-4 md:px-10 mt-6 max-w-[900px] mx-auto">
        <p className="text-sm text-ink-2 mb-5 px-1" data-testid="academy-greeting">
          Hi <span className="font-semibold text-ink">{me.name?.split(" ")[0]}</span> — {me.completed
            ? "your training is complete."
            : doneCount === 0 ? "welcome! Start with lesson 1 below." : "pick up right where you left off."}
        </p>

        {me.completed && <div className="mb-8"><Graduation me={me} hdr={hdr} onUpdate={(d) => setMe((m) => ({ ...m, ...d }))} /></div>}

        {/* Timeline of stages & lessons */}
        <div className="space-y-8">
          {STAGES.map((stage) => {
            const items = LESSONS.map((l, i) => ({ ...l, idx: i })).filter((l) => l.stage === stage.id);
            return (
              <section key={stage.id} data-testid={`academy-stage-${stage.id}`}>
                <div className="flex items-center gap-2.5 mb-3 px-1">
                  <span className="font-mono text-xs font-black" style={{ color: stage.accent }}>STAGE {stage.id}</span>
                  <span className="text-sm font-bold text-ink">{stage.name}</span>
                  <span className="flex-1 h-px" style={{ background: "rgba(15,18,38,0.08)" }} />
                </div>
                <div className="space-y-2.5">
                  {items.map((l) => {
                    const done = me.progress.includes(l.idx);
                    const locked = l.idx > nextIdx;
                    const isActive = active === l.idx;
                    if (isActive) {
                      return (
                        <div key={l.idx} ref={l.idx === active ? cardRef : null} style={{ scrollMarginTop: 90 }}>
                          <LessonCard lesson={l} idx={l.idx} done={done} onDone={markDone} busy={busy}
                            inviteUrl={invite.url} inviteLoading={invite.loading} isNext={l.idx === nextIdx} />
                        </div>
                      );
                    }
                    const Icon = l.icon;
                    return (
                      <button key={l.idx} disabled={locked} onClick={() => setActive(l.idx)}
                        className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl text-left transition-colors duration-200 ${locked ? "cursor-not-allowed" : "hover:bg-white"}`}
                        style={{
                          background: locked ? "rgba(15,18,38,0.03)" : "rgba(255,255,255,0.65)",
                          border: "1px solid rgba(15,18,38,0.07)",
                          opacity: locked ? 0.55 : 1, filter: locked ? "grayscale(1)" : "none",
                        }}
                        data-testid={`lesson-row-${l.idx}`}>
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: done ? "rgba(0,134,58,0.1)" : `${stage.accent}12` }}>
                          {done ? <CheckCircle2 size={17} style={{ color: "#00863A" }} />
                            : locked ? <Lock size={15} className="text-ink-3" />
                            : <Icon size={16} style={{ color: stage.accent }} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-ink truncate">{l.idx + 1}. {l.title}</span>
                          <span className="block text-[11px] font-mono text-ink-3">{l.duration}{done ? " · completed" : locked ? " · locked" : ""}</span>
                        </span>
                        {!locked && <ChevronRight size={16} className="text-ink-3 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-10 text-center text-[11px] font-mono text-ink-3 flex items-center justify-center gap-1.5">
          <GraduationCap size={12} /> Progress saved automatically · resume any time from your email link
        </div>
      </main>
    </div>
  );
}
